import { Application } from '@platform/client';
import { ProfessionalApplicationParameters, UploadResponse } from '@platform/client/schemas';
import { Button, ErrorBox, Icon, InlineFormInput } from '@platform/components';
import { useState } from 'react';
import { FileTrigger } from 'react-aria-components';
import { SubmitHandler, useForm } from 'react-hook-form';
import { ApplicationPageProps } from '../../Application';
import { useApplicationLocalStorage } from '../../application-hook';
import { ApplicationBackground } from '../../ApplicationBackground';
import { ApplicationPageContent } from '../../components/ApplicationPageContent';
import { ApplicationSuccessIcon } from '../../components/ApplicationSuccessIcon';
import BackgroundImage from '../../images/Updatedcomposite.png';

type FormValues = {
  jobTitle: string;
  linkedin: string;
  resume: string;
};

export const ResumeSelection = (props: ApplicationPageProps) => {
  const { onBack, onContinue, progress, isEditProfile, onSaveExit, dataKey } = { ...props };
  const [file, setFile] = useState<File>();
  const [submitted, setSubmitted] = useState(false);

  const { updateApplicationData, getApplicationDataByKey } =
    useApplicationLocalStorage<ProfessionalApplicationParameters>(dataKey);

  const existingResume = getApplicationDataByKey('resume');
  const [isUploaded, setIsUploaded] = useState(!!existingResume);

  const uploadResume = Application.useUploadResume();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<FormValues>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      jobTitle: getApplicationDataByKey('jobTitle'),
      linkedin: getApplicationDataByKey('linkedin'),
      resume: getApplicationDataByKey('resume'),
    },
  });

  const submit: SubmitHandler<FormValues> = (data) => {
    if (file) {
      uploadResume.mutate(
        {
          data: {
            file,
          },
        },
        {
          onSuccess: (response) => {
            updateApplicationData({
              jobTitle: data.jobTitle,
              linkedin: data.linkedin,
              resume: (response as UploadResponse).url,
            });
            onContinue();
          },
        },
      );
    } else if (isEditProfile && (watch('linkedin') || watch('resume'))) {
      updateApplicationData({
        jobTitle: data.jobTitle,
        linkedin: data.linkedin,
        resume: data.resume,
      });
      onContinue();
    } else if ((!file || !watch('resume')) && !watch('linkedin')) {
      setSubmitted(true);
    } else {
      updateApplicationData({
        jobTitle: data.jobTitle,
        linkedin: data.linkedin,
        resume: null,
      });
      onContinue();
    }
  };

  const handleBack = () => {
    updateApplicationData({
      jobTitle: watch('jobTitle'),
      linkedin: watch('linkedin'),
      resume: watch('resume'),
    });
    onBack();
  };

  return (
    <ApplicationBackground
      progress={progress}
      onBack={handleBack}
      onSubmit={handleSubmit(submit)}
      isEditProfile={isEditProfile}
      onSaveAndExit={onSaveExit}
      backgroundImage={BackgroundImage}
    >
      <ApplicationPageContent
        title='Tell us about your experience'
        description='This will appear on your profile, but you can edit it later.'
      >
        <InlineFormInput
          label='Job title'
          placeholder='Enter your current job title'
          register={() =>
            register('jobTitle', { required: 'Enter your current job title to continue' })
          }
          errors={errors.jobTitle}
        />
        <InlineFormInput
          label='LinkedIn'
          placeholder='Enter LinkedIn URL'
          prefixContent='http://'
          register={() => register('linkedin')}
          errors={errors.linkedin}
        />
        <div className='flex gap-3 items-center justify-center'>
          <div className='w-[35%] border-b border-gray-200' />
          Or
          <div className='w-[35%] border-b border-gray-200' />
        </div>
        <FileTrigger
          acceptedFileTypes={[
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'application/pdf',
          ]}
          onSelect={(e) => {
            const files = e ? Array.from(e) : [];
            setFile(files[0]);
            setIsUploaded(true);
            setSubmitted(false);
          }}
        >
          <Button additionalClassnames='p-4 h-[44px] w-full' variant='secondary-gray' size='md'>
            <div className='w-full h-full flex flex-row gap-1.5 items-center justify-center text-gray-600 font-semibold'>
              {isUploaded && <ApplicationSuccessIcon />}
              {!isUploaded && <Icon type='upload-01' size='18' />}
              {isUploaded ? 'Upload complete' : 'Upload resume'}
            </div>
          </Button>
        </FileTrigger>
        {submitted && (
          <ErrorBox showAlertTriangle={true}>Add your LinkedIn or resume to continue</ErrorBox>
        )}
        {/* {isUploaded && <ResumePreviewLink filePath={file?.name ?? watch('resume')} file={file} />} */}
      </ApplicationPageContent>
    </ApplicationBackground>
  );
};
// DG TODO: Mark & Julissa to determine if useful until we update component to include remove options
// const ResumePreviewLink = ({
//   filePath,
//   file,
// }: {
//   filePath?: string | null;
//   file?: File | null;
// }) => {
//   if (!filePath) return null;

//   const isUrl = filePath.startsWith('http://') || filePath.startsWith('https://');
//   const fileName = filePath.split('/').at(-1) ?? 'resume.pdf';

//   const handleLocalPreview = (e: React.MouseEvent<HTMLButtonElement>) => {
//     e.stopPropagation();
//     e.preventDefault();
//     if (!file) return;
//     const url = URL.createObjectURL(file);
//     const tab = window.open(url, '_blank');
//     tab?.addEventListener('load', () => URL.revokeObjectURL(url));
//   };

//   return (
//     <div className='flex justify-center text-xs blue-link -mt-4'>
//       {isUrl ?
//         <a href={filePath} target='_blank' rel='noopener noreferrer'>
//           View uploaded resume
//         </a>
//       : file ?
//         <button aria-label='Preview selected file' onClick={handleLocalPreview}>
//           View uploaded resume
//         </button>
//       : <span>{fileName}</span>}
//     </div>
//   );
// };
